import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { CalendarDays, Truck } from "lucide-react";

const inputCls =
  "focus-visible:ring-2 focus-visible:ring-[#F97316]/30 focus-visible:border-[#F97316]";

export default function Schedule({ schedule, setSchedule, deliveryEstimate, stopEtas = [], onSendToDriver }) {
  const upd = (k, v) => setSchedule({ ...schedule, [k]: v });
  return (
    <div className="bg-white border border-border rounded-lg shadow-sm p-5 space-y-4">
      <div className="flex items-center gap-2">
        <CalendarDays className="h-4 w-4 text-[#F97316]" />
        <h3 className="text-sm font-bold uppercase tracking-[0.1em] text-[#1C1917]">
          Schedule &amp; Requirements
        </h3>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-[11px] font-bold uppercase text-[#57534E]">Pickup date</Label>
          <Input type="date" className={`mt-1 ${inputCls}`} value={schedule.pickupDate} onChange={(e) => upd("pickupDate", e.target.value)} data-testid="pickup-date-input" />
        </div>
        <div>
          <Label className="text-[11px] font-bold uppercase text-[#57534E]">Delivery date</Label>
          <Input type="date" className={`mt-1 ${inputCls}`} value={schedule.deliveryDate} onChange={(e) => upd("deliveryDate", e.target.value)} data-testid="delivery-date-input" />
        </div>
      </div>
      {deliveryEstimate && (
        <div className="rounded-md bg-[#FFF7ED] border border-[#F97316]/30 p-2.5 flex items-start gap-2" data-testid="delivery-estimate">
          <Truck className="h-3.5 w-3.5 shrink-0 mt-0.5 text-[#EA580C]" />
          <div className="text-xs text-[#1C1917] leading-snug">
            In-house estimate: ~{deliveryEstimate.hours}h to last drop → <b>{deliveryEstimate.days} driving day{deliveryEstimate.days > 1 ? "s" : ""}</b> (10h/day max).
            {" "}Est. delivery <b>{deliveryEstimate.date}</b>
            {deliveryEstimate.assumed && <span className="text-[#57534E]"> (assuming pickup today — set a pickup date to refine)</span>}{" "}
            {schedule.deliveryDate !== deliveryEstimate.date && (
              <Button
                variant="link"
                className="h-auto p-0 text-xs text-[#EA580C] font-bold"
                onClick={() => upd("deliveryDate", deliveryEstimate.date)}
                data-testid="use-delivery-estimate"
              >
                Use
              </Button>
            )}
          </div>
        </div>
      )}
      {stopEtas.length > 1 && (
        <div className="rounded-md bg-stone-50 border border-border p-2.5" data-testid="stop-etas">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#57534E]">
            Est. delivery per customer (in-house, 10h/day)
            {stopEtas[0]?.assumed && <span className="normal-case font-normal"> — assuming pickup today</span>}
          </div>
          <div className="mt-1 space-y-0.5">
            {stopEtas.map((e, i) => (
              <div key={`${e.address}-${i}`} className="text-xs text-[#1C1917]" data-testid={`stop-eta-${i}`}>
                <span className="font-semibold">
                  Stop {i + 1}{e.customer ? ` · ${e.customer}` : ""}
                </span>{" "}
                <span className="text-[#57534E]">({e.address})</span> —{" "}
                <b className="text-[#EA580C]">{e.date}</b>{" "}
                <span>(day {e.days})</span>{" "}
                <span className="text-stone-400">~{e.hours}h in</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="space-y-2">
        <label className="flex items-center gap-3 cursor-pointer">
          <Checkbox
            checked={schedule.liftgatePickup}
            onCheckedChange={(v) => upd("liftgatePickup", !!v)}
            className="data-[state=checked]:bg-[#F97316] data-[state=checked]:border-[#F97316]"
            data-testid="liftgate-pickup-checkbox"
          />
          <span className="text-sm text-[#1C1917]">Liftgate required at pickup</span>
        </label>
        <label className="flex items-center gap-3 cursor-pointer">
          <Checkbox
            checked={schedule.liftgateDelivery}
            onCheckedChange={(v) => upd("liftgateDelivery", !!v)}
            className="data-[state=checked]:bg-[#F97316] data-[state=checked]:border-[#F97316]"
            data-testid="liftgate-delivery-checkbox"
          />
          <span className="text-sm text-[#1C1917]">Liftgate required at delivery</span>
        </label>
      </div>
      {onSendToDriver && (
        <Button
          variant="outline"
          size="sm"
          onClick={onSendToDriver}
          className="w-full border-[#1C1917]/30 font-bold"
          data-testid="schedule-send-driver-button"
        >
          <Truck className="h-3.5 w-3.5 mr-1.5" /> Email route to driver / dispatcher
        </Button>
      )}
    </div>
  );
}
